/**
 * Unauthorized Page
 * Displayed when a user attempts to access a resource they don't have permission for
 *
 * Features:
 * - Clear messaging about access denial
 * - Navigation options to go back or to dashboard
 * - Accessible design
 */

import { Box, Typography, Button, Container, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import { PROTECTED_ROUTES } from '@/routes/routes';

/**
 * UnauthorizedPage - Access denied page
 *
 * Shown when a user tries to access a page they don't have permission for.
 * Provides clear messaging and navigation options.
 *
 * @example
 * ```tsx
 * // In route configuration
 * { path: '/unauthorized', element: <UnauthorizedPage /> }
 * ```
 */
export const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoToDashboard = () => {
    navigate(PROTECTED_ROUTES.DASHBOARD);
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: 'transparent',
          }}
        >
          {/* Lock Icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <LockOutlinedIcon
              sx={{ fontSize: 40, color: 'error.contrastText' }}
              aria-hidden="true"
            />
          </Box>

          {/* Title */}
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Access Denied
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}
          >
            You don't have permission to access this page. If you believe this is an error, please
            contact your administrator.
          </Typography>

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              justifyContent: 'center',
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              size="large"
            >
              Go Back
            </Button>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={handleGoToDashboard}
              size="large"
            >
              Go to Dashboard
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default UnauthorizedPage;
